import { Box, Typography } from "@mui/material";
import {
  Home,
  Person,
  CheckCircle,
  MarkEmailRead,
  NotificationsActive,
} from "@mui/icons-material";
import dayjs from "dayjs";
import { ScheduleEntryStatus } from "./DetailedItem";

/** Shared look of the small "tab" icons that sit on the top edge of an event. */
const TAG_ICON_SX = {
  fontSize: 14,
  background: "#fdfdfd",
  border: "1px solid #666",
  borderBottom: 0,
  borderRadius: "4px 4px 0px 0px",
  ":hover": {
    zIndex: 10000,
    scale: 1.5,
    transition: "scale 0.2s ease-in-out",
  },
};

const formatSentAt = (value) =>
  value ? dayjs(value).format("MMM D, h:mm A") : "";

const isDarkTheme = () =>
  typeof document !== "undefined" &&
  document.documentElement.dataset.theme === "dark";

/** Opaque dark tint of the service color so the card stays colored on a dark grid. */
const darkAppointmentFill = (hex) => {
  const raw = String(hex || "#6b7280").replace("#", "");
  const full = raw.length >= 6 ? raw.slice(0, 6) : "6b7280";
  const n = parseInt(full, 16);
  if (Number.isNaN(n)) return "#2a2a2e";
  const mix = (channel) => Math.round(27 + (channel - 27) * 0.1);
  return `rgb(${mix((n >> 16) & 255)}, ${mix((n >> 8) & 255)}, ${mix(n & 255)})`;
};

const TIME_OFF_STRIPES =
  "repeating-linear-gradient(45deg, #e0e0e0, #e0e0e0 6px, #f5f5f5 6px, #f5f5f5 12px)";

const formatGridTime = (date) =>
  date.toLocaleTimeString("en-US", {
    hour12: false,
    hour: "2-digit",
    minute: "2-digit",
  });

/** Grey striped stylist time-off block (JD-465): no client, services or chips. */
const renderTimeOffCell = (props) => {
  const item = props.event;
  return (
    <div
      className="schedule-item schedule-item--time-off"
      style={{
        height: "100%",
        cursor: "pointer",
        border: "1px solid #bdbdbd",
        background: TIME_OFF_STRIPES,
        color: "#424242",
        padding: "2px 4px",
        overflow: "hidden",
      }}
      onClick={(e) => props.onClick?.(e)}
      data-start={formatGridTime(item.start)}
      data-end={formatGridTime(item.end)}
      data-appointment-id={`timeoff-${item.timeOffId}`}
    >
      <Typography sx={{ fontWeight: 800, fontSize: 12, lineHeight: 1.2 }}>
        Time off
      </Typography>
      {item.subtitle ? (
        <Typography
          sx={{
            fontSize: 11,
            lineHeight: 1.2,
            wordBreak: "break-word",
            overflowWrap: "anywhere",
          }}
        >
          {item.subtitle}
        </Typography>
      ) : null}
    </div>
  );
};

export const renderCell = (props, appointmentIdToCopy) => {
  if (props.event?.isTimeOff) return renderTimeOffCell(props);
  const isDisabled = appointmentIdToCopy !== null;
  const item = props.event;
  const isCanceled =
    item.appointment_status === ScheduleEntryStatus.Canceled;
  const formatTime = (date) => {
    const formattedTime = date.toLocaleTimeString("en-US", {
      hour12: false,
      hour: "2-digit",
      minute: "2-digit",
    });
    return formattedTime;
  };

  //Get service duration in minutes (converting from ms)
  const serviceDuration = (item.end - item.start) / 1000 / 60;

  // Determine status indicator color for the top right corner
  const statusIndicatorColor =
    item.appointment_status === ScheduleEntryStatus.NoShow ||
    item.appointment_status === ScheduleEntryStatus.Canceled
      ? "#FF5252" // Red for no show or canceled
      : item.appointment_status === ScheduleEntryStatus.InProgress
      ? "#FFC107" // Yellow for in progress (checked in)
      : item.appointment_status === ScheduleEntryStatus.Finished
      ? "transparent" // Green for checked out
      : "transparent"; // No indicator for other statuses

  const scheduleBorder = "1px solid #666";

  const appointmentGroupId =
    item?.appointmentId ?? item?.event_id ?? item?.eventId ?? "";
  const spineColor =
    item?.services?.[0]?.service_color ||
    item?.services?.[0]?.color ||
    item?.color ||
    "#6b7280";
  const isCheckedOut =
    item.appointment_status === ScheduleEntryStatus.Finished;
  const dark = isDarkTheme();
  // Light mode: near-white tint of the service color.
  // Dark mode: charcoal mixed with that same color, so the card stays tinted.
  // Checked-out blocks use a gray spine and a solid gray fill.
  const displaySpineColor = isCheckedOut ? "#8a8a8a" : spineColor;
  const blockBackground = isCheckedOut
    ? dark
      ? "#3a3a3e"
      : "#d9d9d9"
    : dark
      ? darkAppointmentFill(spineColor)
      : `${spineColor}10`;
  const appointmentText = dark ? "#f2f2f2" : undefined;

  return (
    <div
      draggable={!isCanceled}
      onDragEnd={isCanceled ? undefined : props.onDragEnd}
      onDragEnter={props.onDragEnter}
      onDragOver={props.onDragOver}
      onDragStart={isCanceled ? undefined : props.onDragStart}
      className={`schedule-item`}
      style={{
        cursor: isDisabled ? "not-allowed" : "pointer",
        opacity: isDisabled ? 0.5 : isCanceled ? 0.55 : 1,
        height: "100%",
        padding: "0px",
        display: "flex",
        borderRadius: "0px",
        border: scheduleBorder,
      }}
      onClick={(e) => {
        if (isDisabled) {
          e.preventDefault();
          e.stopPropagation();
          console.log("Click blocked during copy mode");
          return false;
        }
        if (props.onClick) {
          props.onClick(e);
        }
      }}
      data-start={formatTime(item.start)}
      data-end={formatTime(item.end)}
      data-appointment-id={String(appointmentGroupId)}
    >
      <div
        className="schedule-item__spine"
        style={{
          width: "6px",
          minWidth: "6px",
          backgroundColor: displaySpineColor,
          boxShadow: "rgba(0, 0, 0, 0.45) -3px 0px 3px 0px inset",
          borderRight: "1px solid #444",
        }}
      ></div>
      <div
        className="schedule-item__content"
        style={{
          position: "relative",
          height: "100%",
          width: "100%",
          backgroundColor: blockBackground,
        }}
      >
        {item?.services?.map((service, index) => (
          <div
            key={`${service.service_id}-${service.event_id}`}
            style={{
              padding: "2px 2px 2px 4px",
              height: "100%",
              width: "100%",
              overflow: "hidden",
            }}
          >
            {index === 0 && (
              <Typography
                sx={{
                  fontWeight: "800",
                  textTransform: "capitalize",
                  fontSize: serviceDuration > 15 ? 12 : 11,
                  textDecoration:
                    item.appointment_status === ScheduleEntryStatus.Finished ||
                    isCanceled
                      ? "line-through"
                      : "none",
                  lineHeight: serviceDuration > 15 ? "1.1" : "0.9",
                  paddingBottom: serviceDuration > 15 ? "2px" : "0px",
                  wordBreak: "break-word",
                  overflowWrap: "anywhere",
                  color: appointmentText,
                }}
              >
                {item.client?.client_name || ""}
              </Typography>
            )}
            {index === 0 && isCanceled && (
              <Typography
                component="span"
                sx={{
                  fontSize: 9,
                  fontWeight: 700,
                  color: "#FF5252",
                  border: "1px solid #FF5252",
                  borderRadius: "3px",
                  padding: "0px 3px",
                  lineHeight: 1.4,
                  textTransform: "uppercase",
                  display: "inline-block",
                }}
              >
                Cancelled
              </Typography>
            )}
            <div
              style={{
                display: serviceDuration <= 15 ? "flex" : "block",
                flexDirection: serviceDuration <= 15 ? "row-reverse" : "column",
                justifyContent: "start",
                alignItems: "center",
                gap: serviceDuration <= 15 ? "2px" : "0px",
              }}
            >
              <Typography
                sx={{
                  fontSize: serviceDuration > 15 ? 11 : 10,
                  textDecoration:
                    item.appointment_status === ScheduleEntryStatus.Finished ||
                    isCanceled
                      ? "line-through"
                      : "none",
                  lineHeight: serviceDuration > 15 ? "1.1" : "0.9",
                  wordBreak: "break-word",
                  overflowWrap: "anywhere",
                  paddingBottom: serviceDuration > 15 ? "2px" : "0px",
                  color: appointmentText,
                }}
              >
                {service.title}
              </Typography>
              <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                {service.service_shortcut && (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      backgroundColor: `${
                        item.services[0].service_color ||
                        item.services[0].color ||
                        item.color
                      }30`,
                      borderRadius: "2px",
                      padding: serviceDuration > 15 ? "2px 3px" : "1px",
                      width: "fit-content",
                      boxShadow:
                        serviceDuration > 15
                          ? `2px 2px 0px 0px #00000030`
                          : "none",
                      border: `1px solid #00000030`,
                      gap: 4,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: 10,
                        textDecoration:
                          item.appointment_status ===
                            ScheduleEntryStatus.Finished || isCanceled
                            ? "line-through"
                            : "none",
                        lineHeight: "1",
                        wordBreak: "break-word",
                        overflowWrap: "anywhere",
                        fontWeight: "500",
                        color: appointmentText,
                      }}
                    >
                      {service.service_shortcut}
                    </Typography>
                  </div>
                )}
                {index === 0 &&
                  (item.isNewToSalon ||
                    item.isNewToStylist ||
                    item.isClientConfirmed ||
                    item.hasConfirmationEmail ||
                    item.hasReminderEmail) && (
                  <Box
                    sx={{
                      position: "absolute",
                      top: -14,
                      right: 2,
                      zIndex: 9999,
                      display: "flex",
                      flexDirection: "row-reverse",
                      alignItems: "flex-end",
                      gap: "1px",
                    }}
                  >
                    {item.isNewToSalon && (
                      <Home
                        className="schedule-item__tag"
                        sx={{ ...TAG_ICON_SX, color: "#FB5C00" }}
                        titleAccess="New client to salon"
                      />
                    )}
                    {item.isNewToStylist && (
                      <Person
                        className="schedule-item__tag"
                        sx={{ ...TAG_ICON_SX, color: "#C2185B" }}
                        titleAccess="New client to stylist"
                      />
                    )}
                    {item.isClientConfirmed && (
                      <CheckCircle
                        className="schedule-item__tag"
                        sx={{ ...TAG_ICON_SX, color: "#2e7d32" }}
                        titleAccess="Client confirmed attendance"
                      />
                    )}
                    {item.hasConfirmationEmail && (
                      <MarkEmailRead
                        className="schedule-item__tag"
                        sx={{ ...TAG_ICON_SX, color: "#1565c0" }}
                        titleAccess={`Confirmation email sent ${formatSentAt(
                          item.confirmation_email_sent_at
                        )}`}
                      />
                    )}
                    {item.hasReminderEmail && (
                      <NotificationsActive
                        className="schedule-item__tag"
                        sx={{ ...TAG_ICON_SX, color: "#6a1b9a" }}
                        titleAccess={`Reminder email sent ${formatSentAt(
                          item.reminder_email_sent_at
                        )}`}
                      />
                    )}
                  </Box>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
      <div
        style={{
          position: "absolute",
          top: "4px",
          right: "4px",
          width: "10px",
          height: "10px",
          borderRadius: "50%",
          backgroundColor: statusIndicatorColor,
        }}
      ></div>
    </div>
  );
};
